import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { Component, DestroyRef, effect, inject, signal } from '@angular/core';
import { SidenavComponent } from '../components/sidenav/side-nav.component';
import { AuthenticationFacade } from '../../views/authentication/facade/authentication.facade';
import { NavigationFacade } from '../state/navigation/facades/navigation-facade';
import { SideBottomMenuComponent } from '../components/side-bottom-nav-menu/side-bottom-menu';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { BreadcrumbComponent, BreadcrumbService } from 'xng-breadcrumb';
import { ProjectFacade } from '../../views/projects/facades/project.facade';
import { LabFacade } from '../../views/labs/facade/lab.facade';
import { LabSequenceDrawerComponent } from '../../views/labs/components/lab-sequence-drawer/lab-sequence-drawer.component';
import { LabSequenceDrawerState } from '../../views/labs/components/lab-sequence-drawer/lab-sequence-drawer-state';
import { SystemAlertBannerComponent } from '../components/system-alert-banner/system-alert-banner.component';
import { HelpButtonComponent } from '../components/help-button/help-button.component';
import { LabSwitcherComponent } from '../components/lab-switcher/lab-switcher.component';
import { LabInactiveBannerComponent } from '../components/lab-inactive-banner/lab-inactive-banner.component';
import { LabDetailsModalComponent } from '../components/lab-details-modal/lab-details-modal.component';
import { NotificationBellComponent } from '../components/notification-bell/notification-bell.component';
import { TourWelcomeModalComponent } from '../components/tour-welcome-modal/tour-welcome-modal.component';
import { TourDoneModalComponent } from '../components/tour-done-modal/tour-done-modal.component';
import { TourService } from '../services/tour/tour.service';
import { UserMgmtFacade } from '../../views/user-management/facade/user-mgmt.facade';
import { ThemeFacade } from '../state/theme/facades/theme.facade';
import { FontAwesomeSharedModule } from '../font-awesome/font-awesome';
import { shouldHideLabBreadcrumb } from '../utils/lab-page-layout';
import { filter } from 'rxjs';

@Component({
  selector: 'app-layout-container',
  imports: [
    RouterModule,
    SidenavComponent,
    SideBottomMenuComponent,
    CommonModule,
    BreadcrumbComponent,
    LabSequenceDrawerComponent,
    SystemAlertBannerComponent,
    NotificationBellComponent,
    HelpButtonComponent,
    LabSwitcherComponent,
    LabDetailsModalComponent,
    LabInactiveBannerComponent,
    TourWelcomeModalComponent,
    TourDoneModalComponent,
    FontAwesomeSharedModule,
  ],
  templateUrl: './layout-container.component.html',
})
export class LayoutContainerComponent {
  private readonly drawerState = inject(LabSequenceDrawerState);
  isDrawerOpenSignal = this.drawerState.isOpen;
  private breadcrumbService = inject(BreadcrumbService);
  private projectFacade = inject(ProjectFacade);
  private labFacade = inject(LabFacade);
  private auth = inject(AuthenticationFacade);
  private navigationFacade = inject(NavigationFacade);
  private router = inject(Router);
  private userMgmtFacade = inject(UserMgmtFacade);
  readonly themeFacade = inject(ThemeFacade);
  isDark$ = this.themeFacade.isDark$;
  private tourService = inject(TourService);
  private readonly destroyRef = inject(DestroyRef);
  private tourAutoLaunched = false;
  // Tracks the current route so the auto-launch effect re-evaluates on
  // navigation (e.g. after leaving the lab picker for the dashboard).
  private routeUrl = signal(this.router.url);
  selectedMenuItem = signal('');
  userSignal;
  menuItemsSignal;
  isNotificationsRoute = signal(false);

  sideNavMenuItems$;

  constructor() {
    this.userSignal = this.userMgmtFacade.getCurrentUserSignal;

    this.menuItemsSignal = toSignal(this.navigationFacade.sideNavMenuItems$, { initialValue: [] });
    this.sideNavMenuItems$ = this.navigationFacade.sideNavMenuItems$;

    // Only refresh user data on major route changes, not parameter changes
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event: NavigationEnd) => {
        this.routeUrl.set(event.urlAfterRedirects);
        // Only refresh user data when the main route segment changes (not just parameters)
        const currentUrl = event.urlAfterRedirects;
        const previousUrl = this.router.getCurrentNavigation()?.previousNavigation?.finalUrl?.toString();

        // Check if this is a significant route change (not just parameter/query changes)
        const currentPath = currentUrl.split('?')[0].split('#')[0];
        const previousPath = previousUrl?.split('?')[0].split('#')[0];

        if (currentPath !== previousPath) {
          this.userMgmtFacade.getMeUser();
          this.drawerState.reset();
        }
      });

    effect(() => {
      const menuItems = this.menuItemsSignal();
      this.selectedMenuItem.set(menuItems[0]?.name ?? '');
    });
    effect(() => {
      const lab = this.labFacade.getLabDetailSignal();
      if (lab?.display_name) {
        this.breadcrumbService.set('@labDetail', lab.display_name);
      }
    });

    effect(() => {
      const project = this.projectFacade.projectDetailsSignal();
      if (project?.display_name) {
        this.breadcrumbService.set('@projectDetail', project.display_name);
      }
    });

    effect(() => {
      const user = this.userSignal();
      // Read the route so this effect re-runs on navigation, letting auto-launch
      // fire once the user leaves the lab picker for the dashboard.
      this.routeUrl();
      if (!user || this.tourAutoLaunched) return;
      // Lab-scoped users select a lab first; hold the tour until they have one
      // (and until we know their role). Reads the relevant signals so this
      // effect re-runs when the selected lab or current user resolves.
      if (this.tourService.shouldDeferAutoLaunch()) return;
      this.tourAutoLaunched = true;
      this.tourService.maybeStartForFirstTimeUser();
    });
  }

  get currentUser() {
    return this.userSignal();
  }

  // Hide the "Labs / <lab>" breadcrumb for lab users on a lab page (the whole
  // page is one lab, so the path is redundant). Admins keep it.
  hideBreadcrumb(): boolean {
    return shouldHideLabBreadcrumb(
      this.router.url,
      this.userMgmtFacade.getMeUserSignal(),
      this.labFacade.selectedLabSignal()?.id
    );
  }

  public logout() {
    this.auth.postLogout();
    // localStorage.clear() and navigation are handled by the logout success effect
  }
}
